import React from 'react'
import styled from '@emotion/styled'
import Button, { ButtonProps } from 'antd/es/button'
import 'antd/es/button/style/css'
import {
  baseStyle,
  buttonStyle,
  smallButtonStyle
} from 'src/constants/fontStyles'
import { SMALL_SPACING, MEDIUM_SPACING } from 'src/constants/spacing'
import { useTheme } from 'src/shared/themeContext'
import { straitsXTheme } from 'src/shared/theme'

type CustomButtonProps = {
  type?: 'primary' | 'secondary' | 'tertiary'
  size?: 'small'
  iconColor?: 'primary'
  disabled?: boolean
  isFullWidth?: boolean
  children: React.ReactNode
  className?: string
} & Omit<ButtonProps, 'type' | 'disabled'>

const CustomButton = ({
  type = 'primary',
  size,
  iconColor,
  disabled = false,
  isFullWidth = false,
  className = '',
  children,
  ...rest
}: CustomButtonProps) => {
  const { brand, button, actionButton } = useTheme()

  const primaryStyle = `
    background-color: ${brand.action.default};
    color: ${brand.primary.contrast};
    border: none;

    &:focus {
      background-color: ${brand.action.default};
      color: ${brand.primary.contrast};
      border: none;
    }
    
    &:active {
      background-color: ${brand.action.default};
      color: ${brand.primary.contrast};
      border: none;
    }
    &:hover {
      background-color: ${brand.action.hover};
      color: ${brand.primary.contrast};
      border: none;
    }
  `
  const secondaryStyle = `
    background-color: ${button.background};
    color: ${button.color};
    border: 1px solid ${button.border};

    &:focus {
      background-color: ${button.background};
      color: ${button.color};
      border: 1px solid ${button.border};
    }

    &:hover {
      background-color: ${button.background};
      color: ${button.hover};
      border: 1px solid ${button.border};
    }

    &:active {
      background-color: ${button.background};
      color: ${button.color};
      border: 1px solid ${button.border};
    }

    &[ant-click-animating-without-extra-node]:after {
      --antd-wave-shadow-color: ${straitsXTheme.brand.primary.default};
    }
  `

  const tertiaryStyle = `
    background-color: transparent;
    color: ${brand.primary.default};
    border: none;

    &:focus {
      background-color: transparent;
      color: ${brand.primary.default};
      border: none;
    }

    &:hover {
      background-color: transparent;
      color: ${brand.action.hover};
      border: none;
    }

    &:active {
      background-color: transparent;
      color: ${brand.action.default};
      border: none;
    }
  `

  const disabledStyle = `
    cursor: auto;
    pointer-events: none;
    opacity: ${brand.action.disabledOpacity};
  `

  const fullWidthStyle = `
    width: 100%;
  `

  const defaultIconStyle = `
        color: ${actionButton.color};

        &:focus {
            color: ${actionButton.color};
        }

        &:hover {
            color: ${actionButton.hover};
        }

        &:active {
            color: ${actionButton.color};
        }
  `
  const primaryIconStyle = `
        color: ${brand.action.default};

        &:focus {
            color: ${brand.action.default};
        }

        &:hover {
            color: ${brand.action.hover};
        }

        &:active {
            color: ${brand.action.default};
        }
  `

  const StyledButton = styled(Button)`
    &.ant-btn {
      text-shadow: none;
      -webkit-font-smoothing: antialiased;
      border-radius: 5px;
      height: unset;
      box-shadow: none;
      line-height: 0.75;
      ${baseStyle};
      ${size === 'small' ? smallButtonStyle : buttonStyle};
      ${type === 'primary' && primaryStyle}
      ${type === 'secondary' && secondaryStyle}
      ${type === 'tertiary' && tertiaryStyle}
      ${disabled === true && disabledStyle}
      ${isFullWidth && fullWidthStyle}
      padding: ${
        type === 'tertiary'
          ? 0
          : size === 'small'
          ? SMALL_SPACING
          : MEDIUM_SPACING
      };

      & > svg {
        vertical-align: middle;
        font-size: 16px;
        margin-right: 6px;
        ${iconColor === 'primary' ? primaryIconStyle : defaultIconStyle}
      }
    }
  `

  const disabledClassName = disabled ? `straitsx-button-disabled` : undefined

  return (
    <StyledButton className={`${className} ${disabledClassName}`} {...rest}>
      {children}
    </StyledButton>
  )
}

export default CustomButton
